import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useDispatch, useStore } from 'react-redux'
import type { baseApi } from '@/shared/api'
import type { CatalogCartLine, CatalogProductsQuery, ProductPriceKind } from '../model/types'
import { productApi } from './productApi'

/**
 * Корзина, в которую смотрит выдача. Своя корзина клиента делится вкладками цен: строка лежит
 * на вкладке `price.kind` товара. Корзина менеджера — та, что в `cart=` выдачи.
 */
export type CartLineScope =
  { kind: 'own'; settlement: ProductPriceKind } | { kind: 'cart'; cartId: string }

type ApiState = { [baseApi.reducerPath]: ReturnType<typeof baseApi.reducer> }

/** Выдача смотрит в эту корзину: своя — без модификатора, менеджера — по `cart=`. */
function inScope(args: Pick<CatalogProductsQuery, 'client' | 'cart'>, scope: CartLineScope) {
  return scope.kind === 'own'
    ? args.client === undefined && args.cart === undefined
    : args.cart === scope.cartId
}

/**
 * Дописывает товару строку корзины (`cartLine`) в кеш выдачи и карточки после мутации корзины.
 * Бэк говорит о корзине только в своём виде клиента и без id строки (`inCart`, `BE-122`): без
 * записи строка не узнала бы id, а у менеджера — и то, что товар уже в корзине. Значение берётся
 * из ответа той же мутации — сервер его и посчитал, клиент только кладёт на место.
 *
 * Правятся только закешированные запросы той же корзины: вид клиента и полный вид менеджера,
 * своя корзина и корзина за клиента в кеше не смешиваются.
 */
export function useCartLinePatch() {
  const dispatch = useDispatch<ThunkDispatch<ApiState, unknown, UnknownAction>>()
  const store = useStore<ApiState>()

  return (productId: string, scope: CartLineScope, line: CatalogCartLine | null) => {
    const state = store.getState()
    const sameCart = (kind: ProductPriceKind | null) =>
      scope.kind === 'cart' || kind === scope.settlement

    for (const args of productApi.util.selectCachedArgsForQuery(state, 'getProducts')) {
      if (!inScope(args, scope)) continue
      dispatch(
        productApi.util.updateQueryData('getProducts', args, (draft) => {
          for (const row of draft.items) {
            if (row.id === productId && sameCart(row.price.kind)) row.cartLine = line
          }
        }),
      )
    }

    for (const args of productApi.util.selectCachedArgsForQuery(state, 'getProduct')) {
      if (args.id !== productId || !inScope(args, scope)) continue
      dispatch(
        productApi.util.updateQueryData('getProduct', args, (draft) => {
          if (sameCart(draft.price.kind)) draft.cartLine = line
        }),
      )
    }
  }
}
